import { makeStyles, createStyles, IconButton, Typography, Hidden, Select, MenuItem, Button, Dialog, DialogContent, DialogActions, Divider, Checkbox, Theme, DialogTitle, DialogContentText } from '@material-ui/core';
import { red, grey } from '@material-ui/core/colors';
import { Delete, DeleteOutline, FirstPage, KeyboardArrowLeft, KeyboardArrowRight, LastPage, ExpandMore, ExpandLess } from '@material-ui/icons';
import { TemplatedText } from './TemplatedText';
import { LayoutNodeWrapper } from './LayoutNode';
import React, { useState, useContext, FC, ChangeEvent, useCallback, useEffect } from 'react';
import { parseFieldIdentifier, DFSPrimitiveType, SubformDefinition } from '../services';
import { FormContext, useDataValue, useEditability } from '..';
import { FormControlProps, rescopeDataPath, useDataFieldValidity } from '../utils';
import { FormControl } from './FormControl';
import { useConfirmation } from '../ConfirmContext';
import { getDefaultData } from '../../../../utils/state';
import { distinctUntilChanged } from 'rxjs/operators';
import { ErrorObject } from 'ajv';
import moment from 'moment';

export interface ActionBarProps {
  count: number;
  disableCreate: boolean;
}

export interface SubformProps extends FormControlProps<SubformDefinition> {};

const CREATE_BUTTON_WIDTH = '80px';
const useStyles = makeStyles(function(theme: Theme) {
  return createStyles({
    root: {
      flex: 1,
      display: 'flex',
      marginBottom: theme.spacing(2),
      maxWidth: '100%'
    },
    control: {
      display: 'flex',
      flexDirection: 'column',
      margin: theme.spacing(1, 0, 0),
      border: 'thin solid ' + theme.palette.divider,
      maxWidth: '100%'
    },
    header: {
      flex: 1,
      backgroundColor: theme.palette.background.paper,
      cursor: 'pointer',
      'min-height': '36px',
      justifyContent: 'center',
      color: 'inherit',
      display: 'flex',
      alignItems: 'center',
      '&:hover': {
        backgroundColor: '#BDCCEB'
      }
    },
    activeRowHeader: {
      backgroundColor: '#E4EBFA'
    },
    headerRow: {
      flex: 1
    },
    headerTitle: {
      height: '100%',
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      paddingLeft: theme.spacing(1)
    },
    headerRowIndex: {
      width: '1em'
    },
    headerRowContent: {
      flex: 1,
      display: 'flex',
      paddingLeft: theme.spacing(9)
    },
    subformRowContent: {
      flex: 1,
      display: 'flex',
      alignSelf: 'stretch',
      overflow: 'hidden'
    },
    headerColumnText: {
      flex: 1,
      color: theme.palette.text.secondary,
      textAlign: 'center',
      wordBreak: 'break-word',
      alignSelf: 'center',
      verticalAlign: 'middle'
    },
    list: {},
    row: {
      backgroundColor: '#c2c2c2'
    },
    contents: {
      margin: theme.spacing(0, 2, 0),
      backgroundColor: theme.palette.background.paper,
      padding: theme.spacing(2)
    },
    footer: {
      display: 'flex',
      position: 'relative'
    },
    create: {
      marginLeft: theme.spacing(2),
      alignSelf: 'stretch',
      borderRadius: 0
    },
    toolbar: {
      display: 'flex',
      flex: 1,
      padding: 0,
      alignItems: 'center'
    },
    pagingControls: {
      display: 'flex',
      justifyContent: 'center'
    },
    footerText: {
      color: theme.palette.text.secondary,
      marginRight: theme.spacing(1)
    },
    selectText: {
      marginRight: theme.spacing(1)
    },
    spacer: {
      flex: 1
    },
    expandIcon: {
      marginRight: theme.spacing(1)
    },
    rowSelect: {
      paddingRight: theme.spacing(4)
    },
    confirmDeleteButton: {
      color: red[800]
    },
    trashButton: {
      color: red[800]
    },
    actions: {
      display: 'flex',
      justifyContent: 'right'
    },
    done: {
      margin: theme.spacing(2),
      marginLeft: 'auto',
      display: 'flex',
      borderRadius: 0
    },
    duplicate: {
      margin: theme.spacing(2),
      marginLeft: 'auto',
      display: 'flex',
      borderRadius: 0
    },
    delete: {
      margin: theme.spacing(2),
      display: 'flex',
      borderRadius: 0,
      backgroundColor: 'red'
    },
    duplicateModalContent: {
      marginBottom: '120px'
    },
    hasLocalError: {
      backgroundColor: '#d32f2fab',
    }
  })
});
// is this used?
// weird typings
const ActionBar: FC<any> = function(props) {
  const { onChangePage, page, count, rowsPerPage, rowsPerPageOptions, selection, onDelete, onChangeRowsPerPage, onCreate, disabled, disableCreate, canMultiSelect, definition } = props;
  const classes = useStyles({});
  const canGoBack = page === 0;
  const canGoForward = page === Math.max(0, Math.ceil(count / rowsPerPage) - 1);
  const canDelete = Object.keys(selection).length > 0;
  return (
    <div className = { classes.toolbar }>
      <div>
        { canMultiSelect &&
          <React.Fragment>
            <IconButton disabled = { !canDelete || disabled } onClick = { onDelete }>
              { canDelete
                ? <Delete className = { classes.trashButton }></Delete>
                : <DeleteOutline></DeleteOutline>
              }
            </IconButton>
            <Typography variant = { 'caption' }>
              { Object.keys(selection).length + ' selected' }
            </Typography>
          </React.Fragment>
        }
        
      </div>
      <div className = { classes.spacer }></div>
      <Hidden smDown>
        <div className = { classes.footerText }>
          <Typography variant = { 'caption' } className = { classes.selectText }>
            Rows per page
          </Typography>
          <Select
            value = { rowsPerPage }
            onChange = { (event, _) => onChangeRowsPerPage(event) }
            inputProps = { {
              name: 'rows',
              id: 'row-count'
            } }
            classes = { { select: classes.rowSelect } }>
            { rowsPerPageOptions.map((opt: number, idx: number) =>
              <MenuItem key = { idx } value = { opt }>
                { opt }
              </MenuItem>
            )}
          </Select>
        </div>
        <Typography variant = { 'caption' } className = { classes.footerText }>
          { count > 0 ? page * rowsPerPage + 1 + '-' + Math.min(page * rowsPerPage + rowsPerPage, count) + ' of ' + count : '' }
        </Typography>
      </Hidden>
      <div className = { classes.pagingControls }>
        <IconButton
          color = { 'secondary' }
          disabled = { canGoBack }
          onClick = { (e) => onChangePage(e, 0) }>
          <FirstPage></FirstPage>
        </IconButton>
        <IconButton
          color = { 'secondary' }
          disabled = { canGoBack }
          onClick = { (e) => onChangePage(e, page - 1) }>
          <KeyboardArrowLeft></KeyboardArrowLeft>
        </IconButton>
        <IconButton
          color = { 'secondary' }
          disabled = { canGoForward }
          onClick = { (e) => onChangePage(e, page + 1) }>
          <KeyboardArrowRight></KeyboardArrowRight>
        </IconButton>
        <IconButton
          color = { 'secondary' }
          disabled = { canGoForward }
          onClick = { (e) => onChangePage(e, Math.max(0, Math.ceil(count / rowsPerPage) - 1)) }>
          <LastPage></LastPage>
        </IconButton>
      </div>
      {
        !disableCreate && (
          <Button
            className = { classes.create }
            variant = { 'contained' }
            color = { 'secondary' }
            onClick = { onCreate }
            disabled = { disabled }>
              { definition.labels?.new || 'New' }
          </Button>
        )
      }
    </div>
  )
};

export const Subform: FC<SubformProps> = function(props) {
  const { definition, path } = props;
  const classes = useStyles({});
  const [ page, setPage ] = useState(0);
  const [ currentSubform, setCurrentSubform ] = useState(-1);
  const [ rowsPerPage, setRowsPerPage ] = useState(5);
  const [ checked, setChecked ] = useState<{ [ key: string ]: boolean}>({});
  const [ confirmOpen, setConfirmOpen ] = useState(false);
  const formContext = useContext(FormContext);
  const { onSetData, onRemoveData, scope, form, validityMgr } = formContext;
  const dataPath = definition.dataPath;
  const beforeCreate = definition.beforeCreate;
  const maxItems = definition.maxItems;
  const disableCreate = definition.disableCreate;
  const canMultiSelect = definition.multiselect !== false;
  const subforms = useDataValue(definition.dataPath) as undefined | readonly DFSPrimitiveType[];
  const editable = useEditability(definition.editable);
  const rescoped = rescopeDataPath(dataPath, scope);
  const confirm = useConfirmation();
  const [ showDuplicateModal, setShowDuplicateModal ] = useState(false);
  const [ indexForDuplicate, setIndexForDuplicate ] = useState(-1);
  const [ inputForDuplicate, setInputForDuplicate ] = useState<Record<string, any>>();
  const [ isValidForDuplicate, setIsValidForDuplicate ] = useState(false);
  const error = useDataFieldValidity(definition.dataPath);
  const [ localErrors, setLocalErrors ] = useState<ErrorObject[]>([]);
  const sortField = definition.sortField;
  const sortOrder = definition.sortOrder || 'asc';

  useEffect(() => {
    const subscription = validityMgr!.watch('')
      .pipe(
        distinctUntilChanged(),
      )
      .subscribe(function(errors: ErrorObject[]) {
        const fieldIdentifier = parseFieldIdentifier(rescoped);
        const localErrors = errors.filter((error) => error.dataPath.startsWith(fieldIdentifier));
        setLocalErrors(localErrors);
      });
    return () => {
      subscription.unsubscribe();
    }
  }, []);

  const openSubform = () => {
    if (!subforms) {
      Promise.resolve().then(function() {
        return setCurrentSubform(0);
      })
    } else {
      // The new item will be at the last index of the original array
      const newItemIndex = subforms.length;
      Promise.resolve().then(function() {
        return setCurrentSubform(newItemIndex);
      })
    }
  }

  const createItem = useCallback(() => {
    const initialState = getDefaultData(rescoped, form);
    onSetData(dataPath.slice(0, -1).concat('/-}'), scope, initialState);

    openSubform();
  }, [ subforms, dataPath, page, rowsPerPage ])

  const confirmCreate = useCallback(() => {
    if (!beforeCreate) {
      return;
    }

    confirm({
      variant: "warning",
      catchOnCancel: true,
      title: beforeCreate!.prompt.title,
      text: beforeCreate!.prompt.text
    })
      .then(() => {
        createItem();
      })
      .catch((e) => {
        console.warn('Unable to invoke beforeCreate:', e);
      });
  }, [ beforeCreate, confirm, createItem ])

  const handleCreate = useCallback(() => {
    if (beforeCreate) {
      if (beforeCreate.on === 'first') {
        if (subforms?.length === 0) {
          confirmCreate();
        } else {
          createItem();
        }
      } else {
        confirmCreate();
      }
    } else {
      createItem();
    }
  }, [createItem, confirmCreate, beforeCreate]);

  const handleSelectRow = function(checked: { [key: string]: boolean }, idx: number, setChecked: React.Dispatch<React.SetStateAction<{ [key: string]: boolean }>>) {
    return function(event: Event) {
      // let l;
      let newSelection;
      if (checked[idx]) {
        newSelection = Object.assign({}, checked);
        delete newSelection[idx]
      } else {
        newSelection = {
          ...checked,
          [idx]: true
        }
        // newSelection = Object.assign(
        //   Object.assign({}, checked),
        //   { [idx]: true }
        //   // (l = {}, l[idx] = true, l)
        // )
      }
      setChecked(newSelection);
      event.stopPropagation();
    }
  };

  const handleDeleteSelectedRows = function() {
    setConfirmOpen(true)
  };

  const performDelete = function() {
    const toDelete = Object.keys(checked).map(function(str) {
      return parseInt(str);
    }).sort();
    toDelete.reverse().forEach(function(row) {
      console.log('deleting:', '{' + parseFieldIdentifier(definition.dataPath) + '/' + row + '}', scope);
      onRemoveData('{' + parseFieldIdentifier(definition.dataPath) + '/' + row + '}', scope);
    });
    if (currentSubform >= 0 && !toDelete.includes(currentSubform)) {
      const t = toDelete.filter(function(toDelete) {
        return toDelete < currentSubform
      }).length;
      setCurrentSubform(currentSubform - t);
    } else if (toDelete.includes(currentSubform)) {
      setCurrentSubform(-1)
    }
    setChecked({});
    setConfirmOpen(false);
  };
  const handleChangeRowsPerPage = function(event: any) {
    setRowsPerPage(parseInt(event.target.value));
    setPage(0);
    setCurrentSubform(0);
  };
  const handleChangePage = function(_: ChangeEvent, newPage: number) {
    setPage(newPage);
    setCurrentSubform(-1);
  };
  const handleCloseConfirm = function() {
    return setConfirmOpen(false);
  };
  const openDuplicateModal = (idx: number) => {
    setIndexForDuplicate(idx);
    setShowDuplicateModal(true);
  }

  const closeDuplicateModal = () => {
    setShowDuplicateModal(false);
  };

  const handleSingleDelete = (idx: number) => {
    setChecked({[idx]: true});
    setConfirmOpen(true);
  }

  const traverse = (node: any, visitor: (node: any, property: string, value: any) => void) => {
    for (const [property, value] of Object.entries(node)) {
      visitor(node, property, value);
      if (typeof value == "object") {
        traverse(value, visitor);
      }
    }
  }
  
  /**
   * The data to be duplicated can be manipulated to perform some transformations on the target data
   * For example, if the source data has a 'year' property, the new value for year can be sourced from the fields
   * defined under the `contents` node of the `duplication` node, and updated using the `transforms` node
   * Currently two types of transformations are supported
   * - string replacement
   * - replacement from input data for duplication
   * @param data 
   * @returns 
   */
  const duplicateData = (data: any) => {
    const newData = JSON.parse(JSON.stringify(data)); // deep clone
    if (definition.duplication?.transforms) {
      definition.duplication?.transforms.forEach((transform) => {
        let sourceString = '';
        if (transform.from) {
          if (transform.from.type === 'string') {
            sourceString = transform.from.source;
          }
          if (transform.from.type === 'data') {
            sourceString = data[transform.from.source].toString();
          }
        }
        let targetString = '';
        if (transform.to) {
          if (transform.to.type === 'string') {
            targetString = transform.to.target;
          }
          if (transform.to.type === 'data' && inputForDuplicate) {
            targetString = inputForDuplicate[transform.to.target];
          }
        }
        traverse(newData, (node, property, value) => {
          if (typeof node[property] === 'string') {
            node[property] = node[property].replace(sourceString, targetString);
          }
          if (typeof node[property] === 'number' && node[property].toString() === sourceString) {
            node[property] = parseInt(targetString);
          }
        });
      })
    }    
    
    return newData;
  }

  const handleDuplicate = useCallback(() => {
    if (indexForDuplicate > -1 && subforms && subforms[indexForDuplicate]) {
      let data = duplicateData(subforms[indexForDuplicate]);
      onSetData(dataPath.slice(0, -1).concat('/-}'), scope, data);

      Promise.resolve().then(function() {
        closeDuplicateModal();
        return openSubform();
      });
      
    }
    
  }, [ subforms, dataPath, scope, indexForDuplicate, inputForDuplicate ]);

  useEffect(() => {
    let isValid = true;
    if (definition.duplication?.required) {
      if (!inputForDuplicate) {
        isValid = false;
      } else {
        definition.duplication?.required.forEach((fieldName) => {
          isValid = isValid && !!inputForDuplicate[fieldName];
        })
      }
    }
    setIsValidForDuplicate(isValid);
  }, [inputForDuplicate, definition.duplication]);

  // Reusable sort function
  const getSortedItems = useCallback((items: readonly DFSPrimitiveType[]) => {
    const mappedItems = items.map((item: any, originalIndex: number) => ({ item, originalIndex }));
    
    // Return unsorted if no sortField is specified
    if (!sortField) {
      return mappedItems;
    }

    return mappedItems.sort((a: any, b: any) => {
      const aValue = a.item?.[sortField];
      const bValue = b.item?.[sortField];

      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return sortOrder === 'asc' ? 1 : -1;
      if (bValue == null) return sortOrder === 'asc' ? -1 : 1;

      let comparison = 0;

      // Check if values are numbers
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        comparison = aValue - bValue;
      }
      // Check if values are dates (try parsing as date)
      else if (typeof aValue === 'string' && typeof bValue === 'string') {
        const aMoment = moment(aValue);
        const bMoment = moment(bValue);
        
        // If both are valid dates, compare as dates
        if (aMoment.isValid() && bMoment.isValid()) {
          comparison = new Date(aValue).getTime() - new Date(bValue).getTime();
        }
        // Otherwise compare as strings
        else {
          comparison = aValue.localeCompare(bValue);
        }
      }
      // Mixed types or other types - convert to string and compare
      else {
        comparison = String(aValue).localeCompare(String(bValue));
      }

      return sortOrder === 'desc' ? -comparison : comparison;
    });
  }, [sortField, sortOrder]);

  // Handle navigation to newly created items
  useEffect(() => {
    if (subforms && currentSubform >= 0 && currentSubform < subforms.length) {
      let position = currentSubform;

      if (sortField) { 
        // Find where this item appears in the sorted list
        const sortedItems = getSortedItems(subforms);
        
        // Find the position of our current item in the sorted array
        position = sortedItems.findIndex(({ originalIndex }) => originalIndex === currentSubform);
      }
      
      if (position >= 0) {
        // Calculate which page this position is on
        const targetPage = Math.floor(position / rowsPerPage);
        
        if (targetPage !== page) {
          setPage(targetPage);
        }
      }
    }
  }, [subforms, currentSubform, rowsPerPage]);
  
  return (
    <div className = { classes.root }>
      <FormControl
        label = { definition.label }
        style = { definition.style }
        helpText = { definition.helpText }
        footnote={definition.footnote}
        error={error}>
        <Dialog
          open = { confirmOpen }
          onClose = { handleCloseConfirm }
          aria-describedby = { 'confirm-dialog-description' }>
          <DialogContent id = { 'confirm-dialog-description' }>
            Are you sure you want to delete { Object.keys(checked).length } entr{ Object.keys(checked).length === 1 ? 'y' : 'ies' }?
          </DialogContent>
          <DialogActions>
            <Button onClick = { handleCloseConfirm } color = { 'primary' } autoFocus>
              Cancel
            </Button>
            <Button onClick = { performDelete }>
              <span className = { classes.confirmDeleteButton }>
                Delete
              </span>
            </Button>
          </DialogActions>
        </Dialog>
        <div className = { classes.control }>
          <div className = { classes.headerRow }>
            <div className = { classes.headerRowContent }>
              { definition.rowLabel.columns.map((col, i: number) =>
                <div className = { classes.headerColumnText } key = { i }>
                  <Typography variant = { 'subtitle2' }>
                    { col.label }
                  </Typography>
                </div>
              )}
            </div>
            <Divider variant = { 'fullWidth' }></Divider>
          </div>
          <div className = { classes.list }>
            { subforms && (sortField ? getSortedItems(subforms) : subforms.map((item: any, originalIndex: number) => ({ item, originalIndex })))
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map(function({ item, originalIndex }: any, indexInPage: number) {
                const idx = originalIndex;
                const isExpanded = currentSubform === idx;
                const hasLocalError = !!localErrors.filter((error) => {
                  return (
                    error.dataPath.startsWith(parseFieldIdentifier(rescoped) + '/' + (idx) + '/') ||
                    error.dataPath == parseFieldIdentifier(rescoped) + '/' + (idx)
                  );
                }).length;
              
              return (
                <div className = { classes.row } key = { idx }>
                  <FormContext.Provider
                    value = { Object.assign(
                      Object.assign({}, formContext), {
                        scope: parseFieldIdentifier(rescoped) + '/' + (idx)
                      }
                    )}>
                    <div
                      className = { [classes.header, isExpanded ? classes.activeRowHeader : '', hasLocalError ? classes.hasLocalError : ''].join(' ') }
                      onClick = { () => setCurrentSubform(idx === currentSubform ? -1 : idx) }>
                        {/* typings */}
                      { canMultiSelect &&
                        <Checkbox checked = { checked[idx] === true } onClick = { handleSelectRow(checked, idx, setChecked) as any }></Checkbox>
                      }
                      <div className = { classes.headerTitle }>
                        { canMultiSelect &&
                          <Typography variant = { 'caption' } className = { classes.headerRowIndex }>
                            { (page * rowsPerPage) + indexInPage + 1 }.
                          </Typography>
                        }
                        <div className = { classes.subformRowContent }>
                          { definition.rowLabel.columns.map((e, r) =>
                            <div className = { classes.headerColumnText } key = { r }>
                              <TemplatedText template = { e.value } params = { definition.rowLabel.params }></TemplatedText>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className = { classes.expandIcon }>
                        { isExpanded
                          ? <ExpandMore></ExpandMore>
                          : <ExpandLess></ExpandLess>
                        }
                      </div>
                    </div>
                    <Divider variant = { 'fullWidth' }></Divider>
                    { isExpanded &&
                      <React.Fragment>
                        <div
                          className = { classes.contents }
                          style = { {
                            pointerEvents: editable ? 'auto' : 'none',
                            backgroundColor: editable ? undefined : grey[100]
                          } }>
                          <LayoutNodeWrapper path = { path.concat('/contents') }></LayoutNodeWrapper>

                          <div className = { classes.actions }>
                            <Button
                              className = { classes.done }
                              variant = { 'contained' }
                              color = { 'secondary' }
                              onClick = { () => setCurrentSubform(-1) }>
                              { definition.labels?.done || 'Done' }
                            </Button>
                            { definition.duplication &&
                              <Button
                                className = { classes.duplicate }
                                variant = { 'contained' }
                                color = { 'secondary' }
                                onClick = { () => openDuplicateModal(idx) }>
                                  { definition.labels?.duplicate || 'Duplicate' }
                              </Button>
                            }
                            <Button
                              className = { classes.delete }
                              variant = { 'contained' }
                              color = { 'secondary' }
                              onClick = { () => handleSingleDelete(idx) }>
                                { definition.labels?.delete || 'Delete' }
                            </Button>
                          </div>
                        </div>
                      </React.Fragment>
                    }
                    <Divider variant = { 'fullWidth' }></Divider>
                  </FormContext.Provider>
                </div>
              )
            })}
          </div>
        </div>
        <div className = { classes.footer }>
          <ActionBar
            disableCreate = { disableCreate || (typeof maxItems === 'number' && subforms && maxItems <= subforms!.length) }
            count = { subforms !== undefined ? subforms!.length : 0 }
            rowsPerPage = { rowsPerPage }
            page = { page }
            selection = { checked }
            onDelete = { handleDeleteSelectedRows }
            onChangePage = { handleChangePage }
            rowsPerPageOptions = { [5, 10, 15, 25, 50] }
            onChangeRowsPerPage = { handleChangeRowsPerPage }
            onCreate = { handleCreate }
            disabled = { !editable }
            canMultiSelect = { canMultiSelect }
            definition = { definition }>
          </ActionBar>
        </div>
      </FormControl>

      <Dialog
        open = { showDuplicateModal }
        onClose = { closeDuplicateModal }
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <FormContext.Provider
          value = { Object.assign(
            Object.assign({}, formContext), {
              onSetData: (path: string, value: string) => {
                setInputForDuplicate({...inputForDuplicate, [path]: value});
              }
            }
          )}>
          { definition.duplication?.title && <DialogTitle id="alert-dialog-title">
              { definition.duplication?.title }
            </DialogTitle>
          }
          <DialogContent>
            <DialogContentText id="alert-dialog-description" component="div" className = { classes.duplicateModalContent }>
              <LayoutNodeWrapper path = { path.concat('/duplication/contents') }></LayoutNodeWrapper>                  
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={ closeDuplicateModal }>
              Cancel
            </Button>
            <Button onClick={ handleDuplicate } type="submit" autoFocus disabled = { !isValidForDuplicate }>
              { definition.labels?.duplicate || 'Duplicate' }
            </Button>
          </DialogActions>
        </FormContext.Provider>
      </Dialog>
    </div>
  )
};
